import React, { useCallback, useEffect, useRef, useState } from "react";
import mondaySdk from "monday-sdk-js";
import { Check, CloseSmall, ExternalPage, Info } from "@vibe/icons";
import ButtonComponent from "../Button";
import Spinner from "../Spinner";
import logger from "../../lib/logger";
import { analytics } from "../../utils/segment/analyticsService";
import useUser from "../../hooks/context/userHook";
import useItemMergeStream, { type ItemMergeCompletedEvent } from "../../hooks/services/useItemMergeStream";
import { getMergeCandidates, getMergePreview, getMergeStatus, startMerge } from "../../utils/requests/itemMergeRequests";
import MergeItemPicker from "./MergeItemPicker";
import MergeItemReview from "./MergeItemReview";
import {
  allColumnChoices,
  defaultColumnChoices,
  failureMessage,
  INELIGIBLE_REASON_TEXT,
  createMergeOutcomeWaiter,
  type MergeOutcomeTiming,
  type MergeOutcomeWaiter,
  replacedBySide,
} from "./mergeItem";
import type { MergeColumnChoices, MergeNameFrom, MergePreview, MergeRunStatus, MergeTarget } from "./mergeItem.types";

const monday = mondaySdk();

type Step =
  | { kind: "pick" }
  | { kind: "review" }
  | { kind: "running" }
  | { kind: "result"; status: MergeRunStatus | null; timedOut: boolean };

export interface MergeItemFlowProps {
  token: string | null;
  currentItemId: string;
  currentBoardId: string;
  currentItemName: string;
  onClose: () => void;
  /** Injectable for tests: when the status endpoint fallback starts and when to give up. */
  outcomeTiming?: MergeOutcomeTiming;
}

const toStatus = ({ state, reason, failedColumns, otherItemId, otherBoardId }: ItemMergeCompletedEvent): MergeRunStatus => ({
  state,
  ...(reason ? { reason } : {}),
  ...(failedColumns ? { failedColumns } : {}),
  ...(otherItemId ? { otherItemId } : {}),
  ...(otherBoardId ? { otherBoardId } : {}),
});

/**
 * "Merge this item with another item" (monday 13157353371), rendered in place of the Item settings
 * content. The Settings screen is already a monday modal, so this is not a second one.
 *
 * Steps: pick (board → group → item) → review (name + columns) → running → result.
 */
const MergeItemFlow = ({
  token,
  currentItemId,
  currentBoardId,
  currentItemName,
  onClose,
  outcomeTiming,
}: MergeItemFlowProps) => {
  const { userId, accountId } = useUser();
  const [step, setStep] = useState<Step>({ kind: "pick" });
  const [target, setTarget] = useState<MergeTarget | null>(null);
  const [groupBoardIds, setGroupBoardIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [nameFrom, setNameFrom] = useState<MergeNameFrom>("current");
  const [columnChoices, setColumnChoices] = useState<MergeColumnChoices>({});
  const [startError, setStartError] = useState<string | null>(null);
  const mounted = useRef(true);
  const waiter = useRef<{ requestId: string; waiter: MergeOutcomeWaiter } | null>(null);
  // Outcomes that arrive before startMerge has returned the requestId they belong to.
  const earlyOutcomes = useRef(new Map<string, ItemMergeCompletedEvent>());

  // The outcome comes over the notification socket (simb-notification-ms). The status endpoint
  // is only the fallback: on reconnect, since room emits are not buffered, and once overdue.
  useItemMergeStream({
    userId,
    accountId,
    onCompleted: (event) => {
      if (waiter.current?.requestId === event.requestId) waiter.current.waiter.settle(toStatus(event));
      else earlyOutcomes.current.set(event.requestId, event);
    },
    onReconnect: () => waiter.current?.waiter.recheck(),
  });

  useEffect(() => () => waiter.current?.waiter.cancel(), []);

  const track = useCallback(
    (event: string, properties: Record<string, unknown> = {}) => {
      try {
        analytics.track(event, { groupId: `${accountId}`, ...properties });
      } catch {
        // Analytics must never break the flow.
      }
    },
    [accountId],
  );

  useEffect(() => {
    mounted.current = true;
    track("merge_item_opened");
    return () => {
      mounted.current = false;
    };
  }, [track]);

  // The boards this item is already synced to: the picker leaves them out (M6).
  useEffect(() => {
    if (!token) return;
    getMergeCandidates(token, {
      currentItemId,
      currentBoardId,
      candidateBoardId: currentBoardId,
      candidateItemIds: [],
    })
      .then((result) => mounted.current && setGroupBoardIds(result?.groupBoardIds ?? []))
      .catch((error: any) => logger.error("Merge: could not read this item's sync group", error));
  }, [token, currentItemId, currentBoardId]);

  const goToReview = async () => {
    if (!token || !target) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const result = await getMergePreview(token, { currentItemId, otherItemId: target.itemId });
      if (!mounted.current) return;
      if (!result.eligible) {
        setPreviewError(INELIGIBLE_REASON_TEXT[result.reason]);
        return;
      }
      setPreview(result);
      setNameFrom("current");
      setColumnChoices(defaultColumnChoices(result));
      setStep({ kind: "review" });
      track("merge_item_previewed", {
        changing: result.shared.filter((column) => !column.same && column.writable).length,
        shared: result.shared.length,
      });
    } catch (error: any) {
      logger.error("Merge: preview failed", error);
      if (mounted.current) setPreviewError("Could not load the two items. Try again.");
    } finally {
      if (mounted.current) setPreviewLoading(false);
    }
  };

  const merge = async () => {
    if (!token || !preview) return;
    setStartError(null);
    let requestId: string;
    try {
      ({ requestId } = await startMerge(token, {
        currentItemId,
        currentBoardId: preview.current.boardId,
        otherItemId: preview.other.itemId,
        otherBoardId: preview.other.boardId,
        nameFrom,
        columnChoices,
      }));
    } catch (error: any) {
      logger.error("Merge: could not start", error);
      setStartError("The merge could not be started. Nothing was changed. Try again.");
      return;
    }

    const replaced = replacedBySide(preview, nameFrom, columnChoices);
    track("merge_item_confirmed", {
      nameFrom,
      fromCurrent: replaced.onOther.length,
      fromOther: replaced.onCurrent.length,
      groupBoards: preview.groupBoards.length,
    });
    setStep({ kind: "running" });

    const current = createMergeOutcomeWaiter(() => getMergeStatus(token, requestId), outcomeTiming);
    waiter.current = { requestId, waiter: current };
    const early = earlyOutcomes.current.get(requestId);
    if (early) current.settle(toStatus(early));

    const outcome = await current.promise;
    waiter.current = null;
    if (!mounted.current) return;

    track("merge_item_completed", { state: outcome.timedOut ? "timeout" : outcome.status?.state });
    setStep({ kind: "result", status: outcome.status, timedOut: outcome.timedOut });
  };

  const openOther = () => {
    if (!preview) return;
    monday.execute("openItemCard", { itemId: Number(preview.other.itemId) }).catch(() => undefined);
  };

  const header = (title: string, label?: string) => (
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-col gap-1 min-w-0">
        {label && (
          <span className="text-xs font-semibold tracking-wide text-[var(--secondary-text-color)]">{label}</span>
        )}
        <h2 className="m-0 text-xl font-semibold truncate">{title}</h2>
      </div>
    </div>
  );

  if (step.kind === "pick") {
    return (
      <div className="flex flex-col gap-4">
        {header(`Merge “${currentItemName}” with…`, "STEP 1 OF 2")}
        <p className="m-0 text-sm text-[var(--secondary-text-color)]">
          Pick the item on another board that is the same piece of work.
        </p>
        <MergeItemPicker
          token={token}
          currentItemId={currentItemId}
          currentBoardId={currentBoardId}
          currentItemName={currentItemName}
          groupBoardIds={groupBoardIds}
          value={target}
          onChange={(next) => {
            setTarget(next);
            setPreviewError(null);
          }}
        />
        {previewError && (
          <p role="alert" className="m-0 text-sm text-[var(--negative-color)]">
            {previewError}
          </p>
        )}
        <StickyActions justify="end">
          <ButtonComponent text="Cancel" kind="tertiary" onClick={onClose} />
          <ButtonComponent text="Next" disabled={!target} loading={previewLoading} onClick={() => void goToReview()} />
        </StickyActions>
      </div>
    );
  }

  if (step.kind === "review" && preview) {
    return (
      <div className="flex flex-col gap-4">
        {header("Choose the values to keep", "STEP 2 OF 2")}
        <MergeItemReview
          preview={preview}
          nameFrom={nameFrom}
          onNameFromChange={setNameFrom}
          columnChoices={columnChoices}
          onColumnChoiceChange={(columnKey, side) => setColumnChoices((choices) => ({ ...choices, [columnKey]: side }))}
          onUseAll={(side) => {
            setColumnChoices(allColumnChoices(preview, side));
            setNameFrom(side);
          }}
        />
        {startError && (
          <p role="alert" className="m-0 text-sm text-[var(--negative-color)]">
            {startError}
          </p>
        )}
        <StickyActions justify="between">
          <ButtonComponent text="Back" kind="tertiary" onClick={() => setStep({ kind: "pick" })} />
          <ButtonComponent text="Merge items" onClick={() => void merge()} />
        </StickyActions>
      </div>
    );
  }

  if (step.kind === "running") {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-10 text-center" aria-live="polite">
        <Spinner />
        <h2 className="m-0 text-xl font-semibold">Merging…</h2>
        <p className="m-0 max-w-[420px] text-sm text-[var(--secondary-text-color)]">
          Writing the values you picked and linking the two items. This usually takes a few seconds.
        </p>
        <p className="m-0 text-[13px] text-[var(--secondary-text-color)]">
          You can close this window. The item view updates when the merge finishes.
        </p>
      </div>
    );
  }

  if (step.kind === "result") {
    const { status, timedOut } = step;
    const done = status?.state === "done";
    const partial = status?.state === "partial";
    const failedColumns = status?.failedColumns ?? [];

    if (timedOut || status?.state === "unknown") {
      return (
        <ResultLayout icon={<Info aria-hidden />} tone="neutral" title="Still working">
          <p className="m-0 text-sm text-[var(--secondary-text-color)]">
            The merge is taking longer than usual. You can close this; the item view will update when it finishes.
          </p>
          <ButtonComponent text="Done" onClick={onClose} />
        </ResultLayout>
      );
    }

    if (done || partial) {
      return (
        <ResultLayout
          icon={partial ? <Info aria-hidden /> : <Check aria-hidden />}
          tone={partial ? "warning" : "positive"}
          title={
            partial
              ? `Merged, with ${failedColumns.length} ${failedColumns.length === 1 ? "issue" : "issues"}`
              : "Merged"
          }
        >
          {partial ? (
            <>
              <p className="m-0 text-sm text-[var(--secondary-text-color)]">
                The items are linked and stay in sync. These parts did not go through:
              </p>
              <ul className="list-none m-0 p-0 w-full text-left border border-solid border-[#D0D4E4] rounded-md">
                {failedColumns.map((failure, index) => (
                  <li key={`${failure.title}-${index}`} className="px-3.5 py-2.5 border-0 border-b border-solid border-[#E7E9EF] last:border-b-0">
                    <div className="flex justify-between text-sm">
                      <strong className="font-semibold">{failure.title}</strong>
                      <span className="text-[13px] text-[#B25C00]">
                        {failure.boardName ? `Not copied to ${failure.boardName}` : "Not copied"}
                      </span>
                    </div>
                    <span className="text-[13px] text-[var(--secondary-text-color)]">{failure.reason}</span>
                  </li>
                ))}
              </ul>
              <p className="m-0 text-sm text-[var(--secondary-text-color)]">
                Fix them on the board, or use <strong className="text-[var(--primary-text-color)]">Bring up to date</strong>{" "}
                in the item view.
              </p>
            </>
          ) : (
            <p className="m-0 max-w-[420px] text-sm text-[var(--secondary-text-color)]">
              “{nameFrom === "other" ? preview?.other.name : preview?.current.name}” on {preview?.current.boardName} and{" "}
              {preview?.other.boardName} now stay in sync
              {preview?.groupBoards.length ? `, together with ${preview.groupBoards.map((b) => b.boardName).join(", ")}` : ""}.
            </p>
          )}
          <div className="flex gap-2 items-center">
            <ButtonComponent
              text={`Open on ${preview?.other.boardName}`}
              kind="tertiary"
              rightIcon={ExternalPage}
              onClick={openOther}
            />
            <ButtonComponent text="Done" onClick={onClose} />
          </div>
        </ResultLayout>
      );
    }

    return (
      <ResultLayout icon={<CloseSmall aria-hidden />} tone="negative" title="Nothing was merged">
        <p className="m-0 max-w-[440px] text-sm text-[var(--secondary-text-color)]">{failureMessage(status?.reason)}</p>
        <div className="flex gap-2">
          <ButtonComponent text="Back" kind="tertiary" onClick={() => setStep({ kind: "review" })} />
          <ButtonComponent text="Close" onClick={onClose} />
        </div>
      </ResultLayout>
    );
  }

  return null;
};

/**
 * The Settings window cannot grow (a fixed-size monday modal), and the review alone is about twice
 * its height. The step's buttons therefore stick to the bottom of the scrolling pane, so Next and
 * Merge items are always on screen. The negative margins cancel SettingsPage's px-5 py-5, so the
 * bar spans the pane and sits flush with its bottom edge.
 */
const StickyActions = ({ children, justify }: { children: React.ReactNode; justify: "end" | "between" }) => (
  <div
    className={`sticky bottom-0 z-[1] -mx-5 -mb-5 mt-2 px-5 py-3 flex gap-2 bg-white border-0 border-t border-solid border-[#E7E9EF] ${
      justify === "end" ? "justify-end" : "justify-between"
    }`}
  >
    {children}
  </div>
);

const TONE_CLASSES = {
  positive: "bg-[#00CA72] text-white",
  warning: "bg-[#FDAB3D] text-[#323338]",
  negative: "bg-[#FBE9EC] text-[#C4203A]",
  neutral: "bg-[#E6F1FD] text-[#0073EA]",
} as const;

const ResultLayout = ({
  icon,
  tone,
  title,
  children,
}: {
  icon: React.ReactNode;
  tone: keyof typeof TONE_CLASSES;
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col items-center gap-3.5 py-8 text-center" aria-live="polite">
    <span className={`flex items-center justify-center w-12 h-12 rounded-full ${TONE_CLASSES[tone]}`}>{icon}</span>
    <h2 className="m-0 text-xl font-semibold">{title}</h2>
    {children}
  </div>
);

export default MergeItemFlow;
